import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { map, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { Feature } from "@shared/models/feature.interface";
import { TabStateService } from "@shared/services/tab-state/tab-state.service";
import {
	FeatureCheckRequest,
	FeatureCheckResponse,
	AddFeatureRequest,
	EditFeatureRequest,
	FeatureUsageResponse
} from "@shared/models/feature-management";

interface CachedFeatureResult {
	value: boolean;
	timestamp: number;
}

@Injectable({
	providedIn: "root"
})
export class FeatureManagementService {
	private featureCache = new Map<string, CachedFeatureResult>();
	private trackedFeatures = new Set<string>();
	private readonly CACHE_TTL_MS = 10 * 60 * 1000;
	private readonly CACHE_ENABLED_KEY = "feature-management-caching-enabled";
	private cachingEnabled: boolean;

	constructor(private http: HttpClient, private tabStateService: TabStateService) {
		const savedCachingEnabled = localStorage.getItem(this.CACHE_ENABLED_KEY);
		this.cachingEnabled = savedCachingEnabled !== null ? JSON.parse(savedCachingEnabled) : true;
	}

	hasFeature(
		code: string,
		defaultValue = false,
		evaluationContext: Record<string, any> = {
			payGroupId: this.tabStateService.getCustomerEntityGroupSelect().payGroupId,
			legalEntityId: this.tabStateService.getCustomerEntityGroupSelect().legalEntityId,
			customerId: this.tabStateService.getCustomerEntityGroupSelect().customerId
		}
	): Observable<boolean> {
		const cacheKey = `${code}::${JSON.stringify(evaluationContext)}`;

		if (this.cachingEnabled) {
			const cachedResult = this.featureCache.get(cacheKey);
			if (cachedResult) {
				const now = Date.now();
				if (now - cachedResult.timestamp < this.CACHE_TTL_MS) {
					if (!this.trackedFeatures.has(cacheKey)) {
						this.trackedFeatures.add(cacheKey);
						this.trackFeature(code, cachedResult.value, evaluationContext).subscribe();
					}
					return of(cachedResult.value);
				} else {
					this.featureCache.delete(cacheKey);
					this.trackedFeatures.delete(cacheKey);
				}
			}
		}

		const body: FeatureCheckRequest = {
			defaultValue: defaultValue,
			evaluationContext: evaluationContext
		};

		return this.http.post<FeatureCheckResponse>(`${environment.apiUri}/v1/features/${code}`, body).pipe(
			map(response => {
				if (response && typeof response.value === "boolean") {
					return response.value;
				}
				return defaultValue;
			}),
			tap(result => {
				if (this.cachingEnabled) {
					this.featureCache.set(cacheKey, {
						value: result,
						timestamp: Date.now()
					});
				}
			})
		);
	}
	getAllFeatures(): Observable<Feature[]> {
		return this.http.get<Feature[]>(`${environment.apiUri}/v1/feature-management`);
	}

	addFeature(request: AddFeatureRequest): Observable<Feature> {
		return this.http.post<Feature>(`${environment.apiUri}/v1/feature-management`, request);
	}

	editFeature(code: string, request: EditFeatureRequest): Observable<Feature> {
		return this.http.put<Feature>(`${environment.apiUri}/v1/feature-management/${code}`, request);
	}

	getUsage(code: string, startDate: string, endDate: string): Observable<FeatureUsageResponse> {
		const params = {
			startDate: startDate,
			endDate: endDate
		};
		return this.http.get<FeatureUsageResponse>(`${environment.apiUri}/v1/feature-management/${code}/usage`, {
			params
		});
	}

	clearCache(): void {
		this.featureCache.clear();
		this.trackedFeatures.clear();
	}

	clearFeatureCache(code: string, evaluationContext: Record<string, any> = {}): void {
		const cacheKey = `${code}::${JSON.stringify(evaluationContext)}`;
		this.featureCache.delete(cacheKey);
		this.trackedFeatures.delete(cacheKey);
	}

	trackFeature(code: string, returnedValue: any, evaluationContext: Record<string, any> = {}): Observable<any> {
		const body = {
			returnedValue: returnedValue,
			evaluationContext: evaluationContext
		};
		return this.http.post(`${environment.apiUri}/v1/features/${code}/tracking`, body);
	}

	setCachingEnabled(enabled: boolean): void {
		this.cachingEnabled = enabled;
		localStorage.setItem(this.CACHE_ENABLED_KEY, JSON.stringify(enabled));

		if (!enabled) {
			this.clearCache();
		}
	}

	isCachingEnabled(): boolean {
		return this.cachingEnabled;
	}
}
